import { useEffect, useRef, useState } from 'react';

import { stickerEmoji, stickerPath } from '../../../lib/group-content';
import { useBasePath } from '../../../lib/use-base-path';

/**
 * One sticker's animated art (an animated Noto emoji WebP, `public/stickers/<version>/<id>.webp`), drawn `size` px
 * square. The browser plays the WebP itself and decodes it at the size it is drawn. Until the art has loaded, and
 * if it fails, the sticker's emoji stands in, so a sticker is never a blank square. Under "reduce motion" the first
 * frame is drawn still (a canvas keeps only the frame it was given). iOS `AnimatedStickerView` and Android
 * `AnimatedSticker` draw the same art the same way.
 */
export function StickerArt({ stickerId, size }: { stickerId: string | undefined; size: number }) {
  const basePath = useBasePath();
  const path = stickerPath(stickerId);
  const emoji = stickerEmoji(stickerId) ?? '';
  const [state, setState] = useState<'loading' | 'ready' | 'failed'>('loading');
  const reduceMotion = usePrefersReducedMotion();
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);

  useEffect(() => setState('loading'), [path]);
  useEffect(() => {
    if (!reduceMotion || state !== 'ready' || !canvasRef.current || !imgRef.current) return;
    const canvas = canvasRef.current;
    const ratio = typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1;
    canvas.width = Math.round(size * ratio);
    canvas.height = Math.round(size * ratio);
    canvas.getContext('2d')?.drawImage(imgRef.current, 0, 0, canvas.width, canvas.height);
  }, [reduceMotion, state, size]);

  const box = { position: 'relative' as const, width: size, height: size, display: 'block' };
  const showArt = path && state !== 'failed';
  return (
    <span aria-hidden style={box}>
      {state !== 'ready' && (
        <span style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: Math.round(size * 0.7), lineHeight: 1 }}>
          {emoji}
        </span>
      )}
      {showArt && (
        <img
          ref={imgRef}
          src={`${basePath}${path}`}
          alt=""
          width={size}
          height={size}
          decoding="async"
          draggable={false}
          onLoad={() => setState('ready')}
          onError={() => setState('failed')}
          style={{ position: 'absolute', inset: 0, width: size, height: size, opacity: state === 'ready' && !reduceMotion ? 1 : 0 }}
        />
      )}
      {showArt && reduceMotion && state === 'ready' && <canvas ref={canvasRef} style={{ position: 'absolute', inset: 0, width: size, height: size }} />}
    </span>
  );
}

function usePrefersReducedMotion(): boolean {
  const [reduce, setReduce] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const query = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReduce(query.matches);
    const onChange = (e: MediaQueryListEvent) => setReduce(e.matches);
    query.addEventListener?.('change', onChange);
    return () => query.removeEventListener?.('change', onChange);
  }, []);
  return reduce;
}
